/**
 * useKnowledgeBases — the React binding for the Knowledge Bases management
 * feature.
 *
 * Strict layering: the KnowledgeBasesPage calls THIS hook; the hook reaches the
 * backend only through the api facade (`api.knowledgeBase`) and never touches
 * fetch/ApiClient. It owns:
 *   - the registered tenant KBs list (ListTenantKnowledgeBases)
 *   - the account Bedrock KBs pick-list (ListKnowledgeBases) used to prefill
 *   - the "Register a Knowledge Base" form + submit (RegisterKnowledgeBase)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import {
  ApiError,
  KnowledgeBaseApi,
  type KbSummary,
  type TenantKnowledgeBase,
} from '../../api';
import {
  emptyRegisterForm,
  formToRegisterArgs,
  isRegisterFormComplete,
  type RegisterKbForm,
} from './types';
import {
  isTerminalExecutionStatus,
  provisioningPhase,
  sanitizeSectionKey,
  type CreateKbPhase,
} from './kbProvisioning';
import {
  contentTypeForFileName,
  isTextExtractable,
  sanitizeObjectKey,
} from './kbUpload';

/** Poll interval for DescribeExecution while KB provisioning is RUNNING. */
const KB_POLL_INTERVAL_MS = 5000;
/** Safety cap so a stuck provisioning workflow can't poll forever (~5 min). */
const KB_POLL_MAX_ATTEMPTS = 60;

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

export interface UseKnowledgeBasesResult {
  // Registered tenant KBs
  tenantKbs: TenantKnowledgeBase[];
  tenantLoading: boolean;
  tenantError: string | null;
  refreshTenantKbs: () => Promise<void>;

  // Account Bedrock KBs (pick-from list to prefill the form)
  accountKbs: KbSummary[];
  accountLoading: boolean;
  accountError: string | null;
  refreshAccountKbs: () => Promise<void>;

  // Register form
  form: RegisterKbForm;
  setField: (field: keyof RegisterKbForm, value: string) => void;
  prefillFromAccount: (kb: KbSummary) => void;
  resetForm: () => void;
  canRegister: boolean;

  // Register submit
  register: () => Promise<void>;
  registering: boolean;
  /** Result of the last register attempt: whether the KB was registered. */
  registered: boolean | null;
  registerError: string | null;

  // Create a KB by uploading files (provision → upload → ingest → ready)
  /** The current create-by-upload phase. */
  createPhase: CreateKbPhase;
  /** Human-readable current-step detail for the create flow. */
  createStatus: string | null;
  /** Create-flow error message, or null. */
  createError: string | null;
  /** True while a create-by-upload job is in flight. */
  creating: boolean;
  /**
   * Creates a KB for `name` (→ a logical section/bundle key): idempotently
   * provisions it, polls until ready, uploads each file, starts ingestion, then
   * refreshes the registered list. Resolves true on success.
   */
  createKnowledgeBase: (name: string, files: File[]) => Promise<boolean>;
  /** Clears the create-flow status/error back to idle. */
  resetCreate: () => void;

  // Add files to an existing KB (upload → ingest)
  /** The bundle key currently receiving an add-files upload, or null. */
  updatingKey: string | null;
  /** Per-bundle-key add-files status line. */
  updateStatus: Record<string, string>;
  /**
   * Uploads more files to an existing KB's section (by bundle key) then starts
   * ingestion. Resolves true on success.
   */
  addFilesToKb: (bundleKey: string, files: File[]) => Promise<boolean>;
}

function errMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return fallback;
}

export function useKnowledgeBases(): UseKnowledgeBasesResult {
  const api = useApi();

  const [tenantKbs, setTenantKbs] = useState<TenantKnowledgeBase[]>([]);
  const [tenantLoading, setTenantLoading] = useState(false);
  const [tenantError, setTenantError] = useState<string | null>(null);

  const [accountKbs, setAccountKbs] = useState<KbSummary[]>([]);
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountError, setAccountError] = useState<string | null>(null);

  const [form, setForm] = useState<RegisterKbForm>(emptyRegisterForm());
  const [registering, setRegistering] = useState(false);
  const [registered, setRegistered] = useState<boolean | null>(null);
  const [registerError, setRegisterError] = useState<string | null>(null);

  // Create-by-upload state.
  const [createPhase, setCreatePhase] = useState<CreateKbPhase>('idle');
  const [createStatus, setCreateStatus] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);

  // Add-files-to-existing-KB state.
  const [updatingKey, setUpdatingKey] = useState<string | null>(null);
  const [updateStatus, setUpdateStatus] = useState<Record<string, string>>({});

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refreshTenantKbs = useCallback(async () => {
    setTenantLoading(true);
    setTenantError(null);
    try {
      const res = await api.knowledgeBase.listTenantKnowledgeBases();
      setTenantKbs(
        KnowledgeBaseApi.parseTenantKnowledgeBaseList(res.body).knowledgeBases,
      );
    } catch (e) {
      setTenantKbs([]);
      setTenantError(errMessage(e, 'Failed to load registered knowledge bases.'));
    } finally {
      setTenantLoading(false);
    }
  }, [api]);

  const refreshAccountKbs = useCallback(async () => {
    setAccountLoading(true);
    setAccountError(null);
    try {
      const res = await api.knowledgeBase.listKnowledgeBases();
      setAccountKbs(
        KnowledgeBaseApi.parseKnowledgeBaseList(res.body).knowledgeBases,
      );
    } catch (e) {
      setAccountKbs([]);
      setAccountError(errMessage(e, 'Failed to load account knowledge bases.'));
    } finally {
      setAccountLoading(false);
    }
  }, [api]);

  // Load both lists on mount.
  useEffect(() => {
    void refreshTenantKbs();
    void refreshAccountKbs();
  }, [refreshTenantKbs, refreshAccountKbs]);

  const setField = useCallback(
    (field: keyof RegisterKbForm, value: string) => {
      setForm((prev) => ({ ...prev, [field]: value }));
      // Any edit clears the prior register result so stale success/failure
      // never lingers over new input.
      setRegistered(null);
      setRegisterError(null);
    },
    [],
  );

  const prefillFromAccount = useCallback((kb: KbSummary) => {
    setForm((prev) => ({
      ...prev,
      knowledgeBaseId: kb.knowledgeBaseId,
      // Default the bundle key to the KB name (or id) when empty; the user can
      // override. Other fields (data source, bucket, model ARN) aren't part of
      // the account KB summary, so they're left for the user to fill.
      bundleKey: prev.bundleKey.trim() || kb.name || kb.knowledgeBaseId,
    }));
    setRegistered(null);
    setRegisterError(null);
  }, []);

  const resetForm = useCallback(() => {
    setForm(emptyRegisterForm());
    setRegistered(null);
    setRegisterError(null);
  }, []);

  const canRegister = isRegisterFormComplete(form);

  const register = useCallback(async () => {
    if (!isRegisterFormComplete(form)) {
      setRegisterError('Fill in all required fields before registering.');
      return;
    }
    setRegistering(true);
    setRegisterError(null);
    setRegistered(null);
    try {
      const res = await api.knowledgeBase.registerKnowledgeBase(
        formToRegisterArgs(form),
      );
      setRegistered(res.registered);
      if (res.registered) {
        // Refresh the registered list so the new entry appears.
        void refreshTenantKbs();
      }
    } catch (e) {
      setRegisterError(errMessage(e, 'Failed to register knowledge base.'));
    } finally {
      setRegistering(false);
    }
  }, [api, form, refreshTenantKbs]);

  const createKnowledgeBase = useCallback(
    async (name: string, files: File[]): Promise<boolean> => {
      const sectionKey = sanitizeSectionKey(name);
      if (!sectionKey) {
        setCreatePhase('error');
        setCreateError('Enter a valid knowledge base name.');
        return false;
      }
      if (files.length === 0) {
        setCreatePhase('error');
        setCreateError('Add at least one file to create a knowledge base.');
        return false;
      }
      setCreateError(null);
      try {
        // 1) Provision the section-scoped KB (idempotent) and poll to ready —
        //    the same DescribeExecution poll approach the Goal KB panel uses.
        setCreatePhase('provisioning');
        setCreateStatus('Provisioning knowledge base…');
        const prov = await api.knowledgeBase.startKnowledgeBaseProvisioning({
          sectionKey,
        });
        if (!prov.alreadyProvisioned && prov.executionArn) {
          const arn = prov.executionArn;
          let attempts = 0;
          for (;;) {
            const desc = await api.orchestrator.describeExecution({
              executionArn: arn,
            });
            if (!mountedRef.current) return false;
            const phase = provisioningPhase(desc.status);
            if (phase === 'ready') break;
            if (phase === 'error') {
              throw new Error(`Provisioning ${desc.status || 'failed'}.`);
            }
            if (isTerminalExecutionStatus(desc.status)) break; // defensive
            if (++attempts >= KB_POLL_MAX_ATTEMPTS) {
              throw new Error(
                'Provisioning is taking longer than expected. Try again shortly.',
              );
            }
            setCreateStatus(
              `Provisioning knowledge base… (${desc.status || 'RUNNING'})`,
            );
            await delay(KB_POLL_INTERVAL_MS);
          }
        }

        // 2) Upload each file. UploadDocument is text-only (content:string,
        //    no base64/binary field), so binary/PDF is uploaded best-effort as
        //    text and flagged — we don't fake binary support.
        setCreatePhase('uploading');
        const warnings: string[] = [];
        let done = 0;
        for (const file of files) {
          if (!isTextExtractable(file.name)) warnings.push(file.name);
          const content = await file.text();
          await api.knowledgeBase.uploadDocument({
            objectKey: sanitizeObjectKey(file.name),
            content,
            contentType: contentTypeForFileName(file.name),
            bundleKey: sectionKey,
          });
          done += 1;
          if (!mountedRef.current) return false;
          setCreateStatus(`Uploading files… (${done}/${files.length})`);
        }

        // 3) Ingest so the uploaded documents become retrievable.
        setCreatePhase('ingesting');
        setCreateStatus('Starting ingestion…');
        await api.knowledgeBase.startIngestion({ bundleKey: sectionKey });

        // 4) Ready.
        if (!mountedRef.current) return true;
        setCreatePhase('ready');
        setCreateStatus(
          `Knowledge base "${sectionKey}" is ready. Uploaded ${files.length} file(s); ingestion started.` +
            (warnings.length
              ? ` Note: ${warnings.join(', ')} uploaded as text — the upload API is text-only, so binary/PDF fidelity isn't guaranteed.`
              : ''),
        );
        void refreshTenantKbs();
        return true;
      } catch (e) {
        if (!mountedRef.current) return false;
        setCreatePhase('error');
        setCreateError(errMessage(e, 'Failed to create knowledge base.'));
        return false;
      }
    },
    [api, refreshTenantKbs],
  );

  const resetCreate = useCallback(() => {
    setCreatePhase('idle');
    setCreateStatus(null);
    setCreateError(null);
  }, []);

  const addFilesToKb = useCallback(
    async (bundleKey: string, files: File[]): Promise<boolean> => {
      const key = (bundleKey ?? '').trim();
      if (!key || files.length === 0) return false;
      setUpdatingKey(key);
      setUpdateStatus((prev) => ({
        ...prev,
        [key]: `Uploading ${files.length} file(s)…`,
      }));
      try {
        const warnings: string[] = [];
        for (const file of files) {
          if (!isTextExtractable(file.name)) warnings.push(file.name);
          const content = await file.text();
          await api.knowledgeBase.uploadDocument({
            objectKey: sanitizeObjectKey(file.name),
            content,
            contentType: contentTypeForFileName(file.name),
            bundleKey: key,
          });
        }
        await api.knowledgeBase.startIngestion({ bundleKey: key });
        if (!mountedRef.current) return true;
        setUpdateStatus((prev) => ({
          ...prev,
          [key]:
            `Uploaded ${files.length} file(s); ingestion started.` +
            (warnings.length
              ? ` (${warnings.join(', ')} uploaded as text — upload API is text-only.)`
              : ''),
        }));
        void refreshTenantKbs();
        return true;
      } catch (e) {
        if (!mountedRef.current) return false;
        setUpdateStatus((prev) => ({
          ...prev,
          [key]: `⚠️ ${errMessage(e, 'Failed to add files.')}`,
        }));
        return false;
      } finally {
        if (mountedRef.current) setUpdatingKey((k) => (k === key ? null : k));
      }
    },
    [api, refreshTenantKbs],
  );

  const creating =
    createPhase === 'provisioning' ||
    createPhase === 'uploading' ||
    createPhase === 'ingesting';

  return {
    tenantKbs,
    tenantLoading,
    tenantError,
    refreshTenantKbs,
    accountKbs,
    accountLoading,
    accountError,
    refreshAccountKbs,
    form,
    setField,
    prefillFromAccount,
    resetForm,
    canRegister,
    register,
    registering,
    registered,
    registerError,
    createPhase,
    createStatus,
    createError,
    creating,
    createKnowledgeBase,
    resetCreate,
    updatingKey,
    updateStatus,
    addFilesToKb,
  };
}
